'use client';

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import pauseIcon from '@images/ico-banner-pause.svg';
import playIcon from '@images/ico-banner-play.svg';
import { TRACKED_EVENT_POSITION } from '@/constants';
import { TrackedButton } from '@/components/button/tracked-button';
import { useMediaQuery } from '@/hooks/useMediaQuery.hook';
import { useBanner } from './banner-provider.context';
import BannerHiddenButton from './banner-hidden-button';
import { BANNER_HOOK } from './banner-eligibility';

const AUTO_ADVANCE_MS = 5000;

export type BannerSlide = {
  id: string;
  /** 서버에서 렌더한 배너 한 장 */
  node: React.ReactNode;
};

/**
 * 노출 중인 배너를 5초마다 넘긴다. 한 장이면 컨트롤 없이 그대로 보여 준다.
 *
 * 자동 전환은 일시정지 버튼·hover·focus·`prefers-reduced-motion` 에서 멈춘다 —
 * WCAG 2.2 SC 2.2.2 는 5초 넘게 자동으로 움직이는 콘텐츠에 멈출 수단을 요구한다.
 *
 * 슬라이드는 한 칸(grid 1/1)에 겹쳐 쌓는다. 컨테이너 높이가 가장 큰 슬라이드로 고정돼
 * 넘길 때마다 헤더와 본문(`--banner-height`)이 출렁이지 않는다.
 */
export default function BannerCarousel({
  slides,
  doNotShowAgainLabel,
}: {
  slides: BannerSlide[];
  doNotShowAgainLabel: string;
}) {
  const t = useTranslations('Banner');
  const { visibleIds, canHide, isReady } = useBanner();
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
  );
  // prevIndex = 방금 왼쪽으로 나가는 슬라이드. 첫 화면에선 null 이라 들어오는 애니메이션이 없다
  const [{ index, prevIndex }, setPosition] = useState<{
    index: number;
    prevIndex: number | null;
  }>({ index: 0, prevIndex: null });
  // null = 사용자가 아직 누르지 않음 → 모션 설정을 따른다
  const [userPaused, setUserPaused] = useState<boolean | null>(null);
  // hover 와 focus 는 서로 다른 때에 풀리므로 따로 둔다 — 하나로 합치면 마우스가 나갈 때
  // 포커스가 안에 남아 있어도 전환이 재개된다
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const visibleSlides = slides.filter(slide => visibleIds.includes(slide.id));
  const total = visibleSlides.length;
  const isCarousel = total > 1;
  // 판정 전(서버 HTML)엔 total 에 안 보이는 배너도 섞여 있어 "캐러셀·n장 중 1"을 잘못 읽힌다
  const hasCarouselSemantics = isReady && isCarousel;
  const isPaused = userPaused ?? prefersReducedMotion;
  const isRotating = isCarousel && !isPaused && !isHovered && !isFocused;
  // 숨김으로 장수가 줄어도 범위를 벗어나지 않게 한다
  const activeIndex = total > 0 ? index % total : 0;
  const leavingIndex =
    prevIndex !== null && total > 0 && prevIndex % total !== activeIndex
      ? prevIndex % total
      : null;

  const advance = useCallback(
    () =>
      setPosition(prev => ({
        index: (prev.index + 1) % total,
        prevIndex: prev.index,
      })),
    [total],
  );

  useEffect(() => {
    if (!isRotating) return;

    const timer = setTimeout(advance, AUTO_ADVANCE_MS);
    return () => clearTimeout(timer);
    // index 가 바뀔 때마다 5초를 처음부터 다시 잰다
  }, [index, isRotating, advance]);

  const toggleRotation = () => {
    setUserPaused(!isPaused);
    // 재생을 누르면 포커스·hover 가 안에 있어도 바로 재개한다(APG 캐러셀: 회전 컨트롤이 우선)
    if (isPaused) {
      setIsHovered(false);
      setIsFocused(false);
    }
  };
  const toggleText = isPaused ? t('playRotation') : t('pauseRotation');
  const hasControls = isCarousel || canHide;

  return (
    <section
      aria-roledescription={hasCarouselSemantics ? 'carousel' : undefined}
      aria-label={hasCarouselSemantics ? t('carouselLabel') : undefined}
      // 터치 탭은 hover 로 치지 않는다 — 탭 한 번에 다른 곳을 누를 때까지 멈춰 버린다
      onPointerEnter={e => e.pointerType === 'mouse' && setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
      // focus 는 버블링으로 충분하다 — 안쪽 링크·버튼이 포커스를 받으면 여기까지 올라온다
      onFocus={() => setIsFocused(true)}
      onBlur={e => {
        // 안쪽 요소끼리 포커스가 옮겨 갈 때는 멈춘 채로 둔다
        if (!e.currentTarget.contains(e.relatedTarget)) setIsFocused(false);
      }}
      // 슬라이드(`Banner`)가 컨트롤 자리를 비울지 이 값으로 정한다
      data-controls={hasControls}
      className="group/banner relative"
    >
      {/* 자동 전환 중에는 넘길 때마다 읽히지 않게 off, 멈췄을 때만 polite */}
      {/* 밀려 나가는 슬라이드가 배너 밖으로 보이지 않게 잘라 낸다 */}
      <div
        className="grid overflow-hidden"
        aria-live={isRotating ? 'off' : 'polite'}
      >
        {visibleSlides.map((slide, i) => {
          const isActive = i === activeIndex;
          return (
            <div
              key={slide.id}
              // 하이드레이션 전 인계 CSS 가 노출 판정 결과를 이 id 로 적용한다
              {...{ [BANNER_HOOK.SLIDE]: slide.id }}
              role={hasCarouselSemantics ? 'group' : undefined}
              aria-roledescription={hasCarouselSemantics ? 'slide' : undefined}
              aria-label={
                hasCarouselSemantics
                  ? t('slideLabel', { index: i + 1, total })
                  : undefined
              }
              // 안 보이는 슬라이드의 링크가 탭 순서·보조기술에 잡히지 않게 한다.
              // 판정 전(서버 HTML)엔 걸지 않는다 — 인계 CSS 가 보이게 하는 슬라이드가 첫 장이
              // 아닐 수 있는데 inert 면 CTA 를 누를 수 없다. 그동안 안 보이는 슬라이드는
              // visibility:hidden·display:none 이라 포커스·보조기술에서 이미 빠진다.
              inert={isReady && !isActive}
              className={`[grid-area:1/1] ${slideMotionClass(
                isActive,
                i === leavingIndex,
                prevIndex === null,
              )}`}
            >
              {slide.node}
            </div>
          );
        })}
      </div>

      {/* 데스크톱은 배너 높이와 무관하게 세로 중앙, 모바일은 상단 컨트롤 영역(36px) 안 */}
      <div className="absolute right-[20px] top-[14px] flex items-center gap-[3px] md:right-[23px] md:top-1/2 md:-translate-y-1/2">
        {isCarousel && (
          <TrackedButton
            type="button"
            label={isPaused ? 'banner_btn-play' : 'banner_btn-pause'}
            position={TRACKED_EVENT_POSITION.BANNER}
            aria-label={toggleText}
            title={toggleText}
            onClick={toggleRotation}
            className="flex items-center"
            {...{ [BANNER_HOOK.ROTATION]: '' }}
          >
            <Image
              src={isPaused ? playIcon : pauseIcon}
              alt=""
              className="size-[22px] md:size-6"
            />
          </TrackedButton>
        )}
        {canHide && <BannerHiddenButton label={doNotShowAgainLabel} />}
      </div>
    </section>
  );
}

/**
 * 들어오는 슬라이드는 오른쪽에서, 나가는 슬라이드는 왼쪽으로 민다. 나머지는 숨긴다.
 * 클래스가 바뀔 때마다 애니메이션이 처음부터 다시 돈다 — 왼쪽으로 나갔던 슬라이드가 다시
 * 들어올 때도 왼쪽이 아니라 오른쪽에서 들어온다. 동작 줄이기에선 밀지 않고 바로 바꾼다.
 */
function slideMotionClass(
  isActive: boolean,
  isLeaving: boolean,
  isFirstRender: boolean,
) {
  if (isActive)
    return isFirstRender ? '' : 'motion-safe:animate-banner-slide-in';
  if (isLeaving)
    return 'motion-safe:animate-banner-slide-out motion-reduce:invisible';
  return 'invisible';
}
